import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X, Barcode, Keyboard, ArrowsClockwise, Lightning } from "@phosphor-icons/react";

const isExpectedCamError = (e) => {
  const name = e?.name || "";
  const msg = e?.message || "";
  return (
    name === "InvalidStateError" ||
    name === "AbortError" ||
    /invalid state|associated track|track is in|aborted/i.test(msg)
  );
};

// Shared camera selection used by every scanning screen (QR + stock/barcode).
const LAST_CAM_KEY = "tmic_last_camera_id";
const REAR_RE = /back|rear|environment|world/i;
// Prefer the ordinary rear lens; avoid macro / ultra-wide / tele / depth which focus/decode poorly.
const AVOID_RE = /macro|ultra|wide[- ]?angle|tele|depth|zoom|infrared|ir\b/i;

function pickRearDevice(devices, lastId) {
  if (!devices.length) return null;
  if (lastId) {
    const m = devices.find((d) => d.deviceId === lastId);
    if (m) return m;
  }
  const rears = devices.filter((d) => REAR_RE.test(d.label || ""));
  const normalRear = rears.find((d) => !AVOID_RE.test(d.label || ""));
  if (normalRear) return normalRear;
  if (rears.length) return rears[0];
  // No labels (permission not yet granted) or no obvious rear: last device is usually the main rear on Android.
  return devices[devices.length - 1];
}

export default function BarcodeScanner({ onDetected, onClose, title = "Scan barcode / QR", preferRear = false }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const inputRef = useRef(null);
  const streamRef = useRef(null);
  const readerRef = useRef(null);
  const timerRef = useRef(null);
  const doneRef = useRef(false);
  const closingRef = useRef(false);
  const mountedRef = useRef(true);
  const onDetectedRef = useRef(onDetected);
  const [manual, setManual] = useState("");
  const [camError, setCamError] = useState("");
  const [devices, setDevices] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => { onDetectedRef.current = onDetected; });

  const stopTracks = () => {
    try { videoRef.current?.pause(); } catch (e) { /* noop */ }
    try {
      const s = streamRef.current;
      if (s && s.getTracks) s.getTracks().forEach((t) => { try { t.stop(); } catch (e) { /* noop */ } });
    } catch (e) { /* noop */ }
    streamRef.current = null;
  };

  const cleanup = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    doneRef.current = true;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    stopTracks();
    try { if (videoRef.current) videoRef.current.srcObject = null; } catch (e) { /* noop */ }
    readerRef.current = null;
    canvasRef.current = null;
  };

  const fire = (code) => {
    const v = (code || "").trim();
    if (!v || doneRef.current) return;
    cleanup();
    onDetectedRef.current?.(v);
  };

  // (Re)acquire a stream. deviceId when known, otherwise a rear-facing hint. Detects torch support.
  const startStream = async (deviceId) => {
    setCamError("");
    stopTracks();
    let constraints;
    if (deviceId) constraints = { video: { deviceId: { exact: deviceId } }, audio: false };
    else if (preferRear) constraints = { video: { facingMode: { ideal: "environment" } }, audio: false };
    else constraints = { video: true, audio: false };
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (e) {
      if (deviceId) { // exact device failed — retry with a plain rear hint
        try { stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false }); }
        catch (e2) { throw e2; }
      } else { throw e; }
    }
    if (closingRef.current || !mountedRef.current) { stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} }); return; }
    streamRef.current = stream;
    const video = videoRef.current;
    if (!video) { stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} }); return; }
    video.srcObject = stream;
    video.setAttribute("playsinline", "true");
    try { await video.play(); } catch (e) { /* autoplay quirk — decoding still works */ }
    const track = stream.getVideoTracks?.()[0];
    const settings = track?.getSettings?.() || {};
    const activeId = settings.deviceId || deviceId || null;
    if (activeId) { setCurrentId(activeId); try { localStorage.setItem(LAST_CAM_KEY, activeId); } catch (_) {} }
    try {
      const caps = track?.getCapabilities?.() || {};
      setTorchSupported(!!caps.torch);
      setTorchOn(false);
    } catch (_) { setTorchSupported(false); }
    // Enumerate now that permission is granted so labels populate and the picker works.
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices(all.filter((d) => d.kind === "videoinput"));
    } catch (_) { /* noop */ }
  };

  useEffect(() => {
    mountedRef.current = true;
    closingRef.current = false;
    doneRef.current = false;

    const onRejection = (ev) => { if (isExpectedCamError(ev.reason)) ev.preventDefault(); };
    const onError = (ev) => {
      const err = ev?.error || { name: "", message: ev?.message || "" };
      if (isExpectedCamError(err) || isExpectedCamError({ message: ev?.message })) {
        ev.preventDefault?.(); ev.stopImmediatePropagation?.(); return true;
      }
    };
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("error", onError, true);

    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;
    canvasRef.current = document.createElement("canvas");

    const tick = () => {
      if (closingRef.current || doneRef.current || !mountedRef.current) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      try {
        if (video && canvas && video.readyState >= 2 && video.videoWidth > 0) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
          const result = reader.decodeFromCanvas(canvas);
          if (result) { fire(result.getText()); return; }
        }
      } catch (e) { /* NotFound on empty frames — retry */ }
      if (!closingRef.current && mountedRef.current) timerRef.current = setTimeout(tick, 200);
    };

    (async () => {
      try {
        let lastId = null;
        try { lastId = localStorage.getItem(LAST_CAM_KEY); } catch (_) {}
        // First acquire (rear hint / remembered device) to get permission + labels.
        await startStream(lastId || null);
        if (closingRef.current || !mountedRef.current) { cleanup(); return; }
        // With labels available, verify we're on the best rear lens; switch once if not.
        try {
          const all = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput");
          setDevices(all);
          const best = pickRearDevice(all, lastId);
          const activeId = streamRef.current?.getVideoTracks?.()[0]?.getSettings?.().deviceId;
          if (best && best.deviceId && best.deviceId !== activeId && !lastId) {
            await startStream(best.deviceId);
          }
          // Can't identify a rear camera and there are several — let the user choose.
          if (!lastId && all.length > 1 && !all.some((d) => REAR_RE.test(d.label || ""))) setShowPicker(true);
        } catch (_) { /* noop */ }
        timerRef.current = setTimeout(tick, 300);
      } catch (e) {
        if (isExpectedCamError(e) || closingRef.current || !mountedRef.current) return;
        setCamError("Camera unavailable — allow camera access, choose a camera below, or type the code / use a handheld scanner.");
      }
    })();

    inputRef.current?.focus();

    return () => {
      mountedRef.current = false;
      cleanup();
      setTimeout(() => {
        window.removeEventListener("unhandledrejection", onRejection);
        window.removeEventListener("error", onError, true);
      }, 2500);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchCamera = async () => {
    if (devices.length < 2) return;
    const idx = devices.findIndex((d) => d.deviceId === currentId);
    const next = devices[(idx + 1) % devices.length];
    try { await startStream(next.deviceId); } catch (e) { setCamError("Could not switch camera."); }
  };

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    try { await track.applyConstraints({ advanced: [{ torch: !torchOn }] }); setTorchOn((v) => !v); }
    catch (e) { setTorchSupported(false); }
  };

  const chooseDevice = async (id) => { setShowPicker(false); try { await startStream(id); } catch (e) { setCamError("Could not open that camera."); } };

  const handleClose = () => { cleanup(); onClose?.(); };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="barcode-scanner" onClick={handleClose}>
      <div className="bg-white rounded-xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="font-display font-black text-slate-900 flex items-center gap-2"><Barcode size={20} weight="bold" />{title}</h3>
          <button data-testid="barcode-scanner-close" onClick={handleClose}><X size={20} /></button>
        </div>
        <div className="p-4 flex flex-col gap-3">
          <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
            {!camError && <div className="absolute inset-x-8 top-1/2 h-0.5 bg-red-500/80 animate-pulse" />}
            {camError && <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-300 p-4 text-center">{camError}</div>}
            <div className="absolute top-2 right-2 flex gap-2">
              {devices.length > 1 && (
                <button data-testid="barcode-switch-camera" onClick={switchCamera} title="Switch camera"
                  className="h-9 w-9 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70"><ArrowsClockwise size={18} weight="bold" /></button>
              )}
              {torchSupported && (
                <button data-testid="barcode-torch" onClick={toggleTorch} title="Torch"
                  className={`h-9 w-9 flex items-center justify-center rounded-full ${torchOn ? "bg-amber-400 text-slate-900" : "bg-black/50 text-white hover:bg-black/70"}`}><Lightning size={18} weight="bold" /></button>
              )}
            </div>
          </div>

          {(showPicker || devices.length > 1) && (
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Camera</label>
              <select data-testid="barcode-camera-select" value={currentId || ""} onChange={(e) => chooseDevice(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
                {devices.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1"><Keyboard size={14} />Handheld scanner or type it in</label>
            <div className="flex gap-2">
              <Input ref={inputRef} data-testid="barcode-manual-input" value={manual}
                onChange={(e) => setManual(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fire(manual); } }}
                placeholder="Scan or enter barcode, then Enter" className="h-10" />
              <Button data-testid="barcode-manual-submit" onClick={() => fire(manual)} className="h-10 bg-slate-900 font-semibold px-3">Go</Button>
            </div>
          </div>
          <p className="text-xs text-slate-400">Point the rear camera at the code. Use the switch button if the wrong camera opens, or the torch in low light.</p>
        </div>
      </div>
    </div>
  );
}
